import { useRef, type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Gauge, LogOut, Menu, UserCircle, type LucideIcon } from 'lucide-react';
import GuestLanguageSwitcher from '../guest/GuestLanguageSwitcher';
import ThemePreferenceToggle from './ThemePreferenceToggle';
import { useLocale } from '../../context/useLocale';

export type ProductShellNavItem = {
  label: string;
  shortLabel?: string;
  to: string;
  Icon: LucideIcon;
  end?: boolean;
  mobile?: boolean;
  showInPrimaryNav?: boolean;
};

type ProductShellLayoutProps = {
  productName: string;
  productTagline: string;
  ProductIcon: LucideIcon;
  navItems: ProductShellNavItem[];
  productConsoleLabel?: string;
  readOnlyLabel?: string;
  sectionsLabel?: string;
  pinnedLabel?: string;
  signOutLabel?: string;
  userLabel?: string | null;
  onLogout?: () => void;
  children?: ReactNode;
};

export default function ProductShellLayout({
  productName,
  productTagline,
  ProductIcon,
  navItems,
  productConsoleLabel = 'Product console',
  readOnlyLabel = 'Read-only',
  sectionsLabel = 'Sections',
  pinnedLabel = '{count} pinned',
  signOutLabel = 'Sign out',
  userLabel,
  onLogout,
  children,
}: ProductShellLayoutProps) {
  const { locale, setLocale } = useLocale();
  const mobileSectionsRef = useRef<HTMLDetailsElement | null>(null);
  const productReadOnly = /^(1|true|yes|on)$/i.test(import.meta.env.VITE_PRODUCT_READ_ONLY ?? '');
  const primaryNavItems = navItems.filter((item) => item.showInPrimaryNav !== false);
  const mobileNavItems = navItems.filter((item) => item.mobile).length
    ? navItems.filter((item) => item.mobile).slice(0, 5)
    : navItems.slice(0, 5);
  const mobilePinnedLabel = pinnedLabel.replace('{count}', String(mobileNavItems.length));
  const closeMobileSections = () => {
    if (mobileSectionsRef.current) mobileSectionsRef.current.open = false;
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16 text-slate-950 dark:bg-slate-950 dark:text-slate-100 md:pb-0">
      <header className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-950">
                <ProductIcon size={20} aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-base font-semibold text-slate-950 dark:text-slate-50">{productName}</p>
                <p className="truncate text-xs text-slate-500 dark:text-slate-400">{productTagline}</p>
              </div>
            </div>
            <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
              {userLabel ? (
                <div className="hidden min-w-0 items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-xs text-slate-600 dark:border-slate-800 dark:text-slate-300 sm:inline-flex">
                  <UserCircle size={14} aria-hidden="true" />
                  <span className="max-w-48 truncate">{userLabel}</span>
                </div>
              ) : null}
              <div className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-xs text-slate-600 dark:border-slate-800 dark:text-slate-300">
                <Gauge size={14} aria-hidden="true" />
                <span className="hidden sm:inline">{productReadOnly ? readOnlyLabel : productConsoleLabel}</span>
              </div>
              <GuestLanguageSwitcher locale={locale} onChange={setLocale} />
              <ThemePreferenceToggle />
              {onLogout ? (
                <button
                  type="button"
                  onClick={onLogout}
                  title={signOutLabel}
                  aria-label={signOutLabel}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600 transition hover:bg-slate-100 hover:text-slate-950 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-slate-50"
                >
                  <LogOut size={16} aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </div>
          <details ref={mobileSectionsRef} className="md:hidden">
            <summary className="flex cursor-pointer list-none items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 [&::-webkit-details-marker]:hidden">
              <span className="inline-flex items-center gap-2">
                <Menu size={16} aria-hidden="true" />
                {sectionsLabel}
              </span>
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{mobilePinnedLabel}</span>
            </summary>
            <nav className="mt-2 grid gap-2" aria-label={`${productName} mobile navigation`}>
              {primaryNavItems.map(({ label, shortLabel, to, Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={closeMobileSections}
                  className={({ isActive }) =>
                    [
                      'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition',
                      isActive
                        ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-950'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-slate-50',
                    ].join(' ')
                  }
                >
                  <Icon size={16} aria-hidden="true" />
                  {shortLabel ?? label}
                </NavLink>
              ))}
            </nav>
          </details>
          <nav className="hidden gap-1 overflow-x-auto md:flex" aria-label={`${productName} navigation`}>
            {primaryNavItems.map(({ label, to, Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  [
                    'inline-flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition',
                    isActive
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-950'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-slate-50',
                  ].join(' ')
                }
              >
                <Icon size={16} aria-hidden="true" />
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">{children ?? <Outlet />}</main>
      <nav
        className="fixed inset-x-0 bottom-0 z-40 grid border-t border-slate-200 bg-white/95 shadow-lg backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 md:hidden"
        style={{ gridTemplateColumns: `repeat(${Math.max(1, mobileNavItems.length)}, minmax(0, 1fr))` }}
        aria-label={`${productName} primary mobile navigation`}
      >
        {mobileNavItems.map(({ label, shortLabel, to, Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={closeMobileSections}
            className={({ isActive }) =>
              [
                'flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 px-1 text-[11px] font-semibold transition',
                isActive
                  ? 'text-slate-950 dark:text-slate-50'
                  : 'text-slate-500 hover:text-slate-950 dark:text-slate-400 dark:hover:text-slate-50',
              ].join(' ')
            }
          >
            <Icon size={18} aria-hidden="true" />
            <span className="w-full truncate text-center">{shortLabel ?? label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
